import React, { useState } from 'react';

export type AiPersona = {
  name: string;
  segment: string;
  situation: string;
  needs: string;
  searchIntent: string;
  painPoints: string[];
  messages: string[];
  suggestedActions: string[];
};

export type AiPersonasData = {
  keyword: string;
  service: string;
  headline: string;
  summary: string;
  personas: AiPersona[];
  sharedNeeds: string[];
  marketingHints: string[];
};

type Props = {
  data: AiPersonasData | null;
  cached: boolean;
  loading: boolean;
  error: string | null;
  personaImages: Record<string, string>;
  personaImageLoading: Record<string, boolean>;
  personaImageErrors: Record<string, string>;
  onFetch: () => void;
  onForceFetch?: () => void;
  onGeneratePersonaImage: (persona: AiPersona, index: number, force?: boolean) => void;
};

const getPersonaKey = (persona: AiPersona, index: number) => `${index}:${persona.name}:${persona.segment}`;

const AiPersonasCard: React.FC<Props> = ({
  data,
  cached,
  loading,
  error,
  personaImages,
  personaImageLoading,
  personaImageErrors,
  onFetch,
  onForceFetch,
  onGeneratePersonaImage,
}) => {
  const [copied, setCopied] = useState(false);
  const [copiedPersonaName, setCopiedPersonaName] = useState<string | null>(null);
  const [copiedPersonaImageKey, setCopiedPersonaImageKey] = useState<string | null>(null);

  const wrapCanvasText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    lineHeight: number,
    maxLines = 3
  ) => {
    const chars = String(text || '').split('');
    const lines: string[] = [];
    let line = '';

    for (const char of chars) {
      const nextLine = line + char;
      if (ctx.measureText(nextLine).width > maxWidth && line) {
        lines.push(line);
        line = char;
        if (lines.length >= maxLines) break;
      } else {
        line = nextLine;
      }
    }

    if (line && lines.length < maxLines) lines.push(line);

    lines.forEach((lineText, index) => {
      const suffix = index === maxLines - 1 && chars.join('').length > lines.join('').length ? '...' : '';
      ctx.fillText(`${lineText}${suffix}`, x, y + index * lineHeight);
    });

    return y + lines.length * lineHeight;
  };

  const drawBulletList = (
    ctx: CanvasRenderingContext2D,
    items: string[],
    x: number,
    y: number,
    maxWidth: number,
    maxItems = 3
  ) => {
    let currentY = y;
    items.slice(0, maxItems).forEach((item) => {
      ctx.fillText('•', x, currentY);
      currentY = wrapCanvasText(ctx, item, x + 22, currentY, maxWidth - 22, 28, 2) + 8;
    });
    return currentY;
  };

  const downloadPersonaImage = (persona: AiPersona, index: number) => {
    const canvas = document.createElement('canvas');
    canvas.width = 1200;
    canvas.height = 900;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = '#ffffff';
    ctx.roundRect(48, 48, 1104, 804, 28);
    ctx.fill();

    ctx.fillStyle = '#4f46e5';
    ctx.roundRect(48, 48, 1104, 170, 28);
    ctx.fill();
    ctx.fillRect(48, 170, 1104, 48);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.18)';
    ctx.beginPath();
    ctx.arc(1010, 112, 96, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.font = '700 42px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.fillText(persona.name || `ペルソナ${index + 1}`, 92, 112);

    ctx.font = '600 22px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.fillStyle = '#c7d2fe';
    wrapCanvasText(ctx, persona.segment, 94, 154, 720, 30, 2);

    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(1010, 120, 54, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#4f46e5';
    ctx.font = '700 36px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText((persona.name || '仮説').slice(0, 2), 1010, 120);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';

    ctx.fillStyle = '#111827';
    ctx.font = '700 24px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.fillText('状況', 92, 278);
    ctx.fillText('ニーズ', 92, 424);
    ctx.fillText('刺さる訴求', 92, 596);
    ctx.fillText('導線案', 650, 596);

    ctx.font = '400 24px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.fillStyle = '#374151';
    wrapCanvasText(ctx, persona.situation, 92, 318, 980, 34, 3);
    wrapCanvasText(ctx, persona.needs, 92, 464, 980, 34, 3);

    ctx.font = '400 21px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    drawBulletList(ctx, persona.messages, 92, 638, 480, 4);
    drawBulletList(ctx, persona.suggestedActions, 650, 638, 430, 4);

    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(92, 560);
    ctx.lineTo(1108, 560);
    ctx.stroke();

    ctx.fillStyle = '#64748b';
    ctx.font = '600 18px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.fillText('NeedRadar / Persona Hypothesis', 92, 820);
    if (data?.keyword) {
      ctx.textAlign = 'right';
      ctx.fillText(data.keyword, 1108, 820);
      ctx.textAlign = 'left';
    }

    const link = document.createElement('a');
    link.download = `persona-${index + 1}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const buildPersonaMarkdown = (persona: AiPersona, index?: number) => [
    `### ${typeof index === 'number' ? `${index + 1}. ` : ''}${persona.name}`,
    '',
    '| 項目 | 内容 |',
    '|---|---|',
    `| セグメント | ${persona.segment || '-'} |`,
    `| 状況 | ${persona.situation || '-'} |`,
    `| ニーズ | ${persona.needs || '-'} |`,
    `| 検索意図 | ${persona.searchIntent || '-'} |`,
    '',
    '#### 困りごと',
    ...(persona.painPoints.length > 0 ? persona.painPoints.map((item) => `- ${item}`) : ['- -']),
    '',
    '#### 刺さる訴求',
    ...(persona.messages.length > 0 ? persona.messages.map((item) => `- ${item}`) : ['- -']),
    '',
    '#### 導線案',
    ...(persona.suggestedActions.length > 0 ? persona.suggestedActions.map((item) => `- ${item}`) : ['- -']),
  ].join('\n');

  const buildCopyText = () => {
    if (!data) return '';

    return [
      `# ペルソナ仮説: ${data.keyword}`,
      '',
      `## ${data.headline}`,
      data.summary,
      '',
      '## ペルソナ',
      ...data.personas.flatMap((persona, index) => [buildPersonaMarkdown(persona, index), '']),
      '## 共通ニーズ',
      ...data.sharedNeeds.map((item) => `- ${item}`),
      '',
      '## マーケティング示唆',
      ...data.marketingHints.map((item) => `- ${item}`),
    ].filter(Boolean).join('\n');
  };

  const handleCopy = async () => {
    if (!data) return;

    try {
      await navigator.clipboard.writeText(buildCopyText());
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch (copyError) {
      console.error('ペルソナ仮説のコピーに失敗:', copyError);
    }
  };

  const handlePersonaCopy = async (persona: AiPersona, index: number) => {
    try {
      await navigator.clipboard.writeText(buildPersonaMarkdown(persona, index));
      setCopiedPersonaName(persona.name);
      window.setTimeout(() => setCopiedPersonaName(null), 1400);
    } catch (copyError) {
      console.error('個別ペルソナのコピーに失敗:', copyError);
    }
  };

  const downloadGeneratedPersonaImage = (imageUrl: string, persona: AiPersona, index: number) => {
    const link = document.createElement('a');
    link.download = `persona-image-${index + 1}-${persona.name || 'persona'}.png`;
    link.href = imageUrl;
    link.click();
  };

  const copyGeneratedPersonaImage = async (imageUrl: string, persona: AiPersona, index: number) => {
    const key = getPersonaKey(persona, index);

    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ [blob.type || 'image/png']: blob })
      ]);
      setCopiedPersonaImageKey(key);
      window.setTimeout(() => setCopiedPersonaImageKey(null), 1400);
    } catch (copyError) {
      console.error('ペルソナ画像のコピーに失敗:', copyError);
    }
  };

  return (
    <section className="bg-white shadow-sm rounded-lg p-6 min-w-0">
      <div className="flex flex-col gap-3 border-b pb-2 mb-4 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-bold text-gray-800">ペルソナ仮説 β</h2>
            {cached && (
              <span className="text-[11px] font-semibold px-2 py-1 rounded bg-emerald-50 text-emerald-700">
                保存済みデータを表示中
              </span>
            )}
          </div>
          <p className="mt-1 text-[11px] text-gray-500">検索者の立場・状況・刺さる訴求を推定</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {data && (
            <button
              type="button"
              onClick={handleCopy}
              className={`px-3 py-2 rounded text-sm font-semibold text-white ${
                copied ? 'bg-emerald-500' : 'bg-slate-600 hover:bg-slate-700'
              }`}
            >
              {copied ? 'コピー済み' : 'Markdownコピー'}
            </button>
          )}
          {data && onForceFetch && (
            <button
              type="button"
              onClick={onForceFetch}
              disabled={loading}
              className="rounded border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              再生成
            </button>
          )}
          <button
            type="button"
            onClick={() => onFetch()}
            disabled={loading}
            className="bg-blue-500 text-white px-3 py-2 rounded text-sm font-semibold hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? '生成中...' : 'ペルソナを生成'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {error}
        </div>
      )}

      {!data ? (
        <div className="h-[180px] flex items-center justify-center text-center text-sm text-gray-500">
          ボタンを押すとAIが検索者のペルソナ仮説を生成します
        </div>
      ) : (
        <div className="space-y-5">
          <div className="rounded border border-indigo-100 bg-indigo-50 p-4">
            <p className="text-xs font-semibold text-indigo-600">{data.keyword}</p>
            <h3 className="mt-1 text-xl font-bold text-gray-900">{data.headline}</h3>
            <p className="mt-2 text-sm leading-6 text-gray-700">{data.summary}</p>
          </div>

          {data.personas.length > 0 && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
              {data.personas.map((persona, index) => (
                <div key={`${persona.name}-${persona.segment}`} className="rounded border border-gray-100 p-4">
                  {(() => {
                    const imageKey = getPersonaKey(persona, index);
                    const generatedImage = personaImages[imageKey];
                    const imageLoading = Boolean(personaImageLoading[imageKey]);
                    const imageError = personaImageErrors[imageKey];

                    return (
                      <>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <h4 className="font-bold text-gray-800">{persona.name}</h4>
                      <p className="mt-1 text-xs font-semibold text-indigo-600">{persona.segment}</p>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <span className="rounded bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600">
                        {persona.searchIntent || '検索意図'}
                      </span>
                      <button
                        type="button"
                        onClick={() => handlePersonaCopy(persona, index)}
                        className={`rounded px-2 py-1 text-[11px] font-semibold text-white ${
                          copiedPersonaName === persona.name ? 'bg-emerald-500' : 'bg-slate-500 hover:bg-slate-600'
                        }`}
                      >
                        {copiedPersonaName === persona.name ? 'コピー済み' : 'コピー'}
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadPersonaImage(persona, index)}
                        className="rounded bg-indigo-500 px-2 py-1 text-[11px] font-semibold text-white hover:bg-indigo-600"
                      >
                        カード保存
                      </button>
                      <button
                        type="button"
                        onClick={() => onGeneratePersonaImage(persona, index)}
                        disabled={imageLoading}
                        className="rounded bg-fuchsia-500 px-2 py-1 text-[11px] font-semibold text-white hover:bg-fuchsia-600 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {imageLoading ? '生成中...' : 'イメージ生成'}
                      </button>
                    </div>
                  </div>

                  {imageError && (
                    <div className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                      {imageError}
                    </div>
                  )}

                  {generatedImage && (
                    <div className="mt-3 rounded border border-indigo-100 bg-indigo-50 p-3">
                      <img
                        src={generatedImage}
                        alt={`${persona.name}のイメージ図`}
                        className="mx-auto max-h-[320px] w-full rounded border border-white object-contain shadow-sm"
                      />
                      <div className="mt-2 flex flex-wrap justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => copyGeneratedPersonaImage(generatedImage, persona, index)}
                          className={`rounded px-2 py-1 text-[11px] font-semibold text-white ${
                            copiedPersonaImageKey === imageKey ? 'bg-emerald-500' : 'bg-slate-500 hover:bg-slate-600'
                          }`}
                        >
                          {copiedPersonaImageKey === imageKey ? 'コピー済み' : '画像コピー'}
                        </button>
                        <button
                          type="button"
                          onClick={() => onGeneratePersonaImage(persona, index, true)}
                          disabled={imageLoading}
                          className="rounded border border-indigo-200 bg-white px-2 py-1 text-[11px] font-semibold text-indigo-600 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          再生成
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadGeneratedPersonaImage(generatedImage, persona, index)}
                          className="rounded bg-indigo-500 px-2 py-1 text-[11px] font-semibold text-white hover:bg-indigo-600"
                        >
                          画像保存
                        </button>
                      </div>
                    </div>
                  )}

                  <dl className="mt-3 space-y-2 text-sm">
                    <div>
                      <dt className="font-semibold text-gray-500">状況</dt>
                      <dd className="mt-1 leading-6 text-gray-700">{persona.situation}</dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-gray-500">ニーズ</dt>
                      <dd className="mt-1 leading-6 text-gray-700">{persona.needs}</dd>
                    </div>
                  </dl>

                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs font-bold text-gray-500">困りごと</p>
                      <ul className="mt-1 space-y-1 text-sm text-gray-600">
                        {persona.painPoints.map((item) => <li key={item}>・{item}</li>)}
                      </ul>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-500">刺さる訴求</p>
                      <ul className="mt-1 space-y-1 text-sm text-gray-600">
                        {persona.messages.map((item) => <li key={item}>・{item}</li>)}
                      </ul>
                    </div>
                  </div>

                  {persona.suggestedActions.length > 0 && (
                    <div className="mt-3 rounded bg-slate-50 p-3">
                      <p className="text-xs font-bold text-gray-500">導線案</p>
                      <ul className="mt-1 space-y-1 text-sm text-gray-600">
                        {persona.suggestedActions.map((item) => <li key={item}>・{item}</li>)}
                      </ul>
                    </div>
                  )}
                      </>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="rounded border border-gray-100 p-4">
              <h4 className="text-sm font-bold text-gray-800">共通ニーズ</h4>
              <ul className="mt-2 space-y-2 text-sm text-gray-600">
                {data.sharedNeeds.map((item) => <li key={item}>・{item}</li>)}
              </ul>
            </div>
            <div className="rounded border border-gray-100 p-4">
              <h4 className="text-sm font-bold text-gray-800">マーケティング示唆</h4>
              <ul className="mt-2 space-y-2 text-sm text-gray-600">
                {data.marketingHints.map((item) => <li key={item}>・{item}</li>)}
              </ul>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AiPersonasCard;
